import { SupabaseClient } from '@supabase/supabase-js';
import { ProjectRepository } from '../repositories/project.repository';
import { ProjectUserRepository } from '../repositories/project-user.repository';
import { UserRepository } from '../repositories/user.repository';
import { ProjectInvitationRepository } from '../repositories/project-invitation.repository';
import { Project, ProjectInsert } from '../entities/project';
import { ProjectUser, ProjectUserInsert, ProjectUserRole } from '../entities/project-user';
import { ProjectInvitation, InvitationStatus } from '../entities/project-invitation';
import { ProjectDTO, CreateProjectDTO, UpdateProjectDTO } from '../dtos';
import { ProjectUserDTO, ProjectInvitationWithProjectDTO } from '../dtos';
import { ProjectMapper } from '../mappers/project.mapper';
import { ProjectInvitationMapper } from '../mappers/project-invitation.mapper';
import { PaginationOptions, PaginatedResponse } from '../types/pagination.types';
import { UserRole } from '../types/user-role.enum';

interface AddUserToProjectData {
  projectId: string;
  userId: string;
  role: UserRole;
}

interface UpdateUserRoleData {
  projectId: string;
  userId: string;
  newRole: UserRole;
}

export class ProjectService {
  private projectRepository: ProjectRepository;
  private projectUserRepository: ProjectUserRepository;
  private userRepository: UserRepository;
  private projectInvitationRepository: ProjectInvitationRepository;

  constructor(private supabase: SupabaseClient) {
    this.projectRepository = new ProjectRepository(supabase);
    this.projectUserRepository = new ProjectUserRepository(supabase);
    this.userRepository = new UserRepository(supabase);
    this.projectInvitationRepository = new ProjectInvitationRepository(supabase);
  }

  /**
   * 8자리 고유 초대 코드 생성
   */
  private async generateUniqueInviteCode(): Promise<string> {
    const maxAttempts = 100;

    for (let i = 0; i < maxAttempts; i++) {
      // 8자리 랜덤 숫자 생성 (00000000 ~ 99999999)
      const code = Math.floor(Math.random() * 100000000).toString().padStart(8, '0');

      // 중복 확인
      const isUnique = await this.projectRepository.isInviteCodeUnique(code);
      if (isUnique) {
        return code;
      }
    }

    throw new Error('Failed to generate unique invite code after 100 attempts');
  }

  /**
   * 프로젝트 생성 - 생성자는 자동으로 owner가 됨
   */
  async createProject(data: CreateProjectDTO, userId: string): Promise<ProjectDTO> {
    try {
      
      // 1. 고유한 초대 코드 생성
      const inviteCode = await this.generateUniqueInviteCode();

      // 2. 프로젝트 생성
      const project = await this.projectRepository.create({
        name: data.name,
        description: data.description,
        invite_code: inviteCode,
        created_by_id: userId
      });

      // 3. 생성자를 owner로 추가
      try {
        const projectUser = await this.projectUserRepository.create({
          project_id: project.id,
          user_id: userId,
          role: UserRole.OWNER
        });
      } catch (error) {
        // 프로젝트 생성은 성공했지만 owner 추가에 실패한 경우 프로젝트 삭제
        await this.projectRepository.delete(project.id);
        throw error;
      }

      // 4. 생성된 프로젝트를 현재 프로젝트로 설정
      await this.userRepository.updateCurrentProject(userId, project.id);

      return ProjectMapper.toDTO(project);
    } catch (error) {
      throw new Error(`Failed to create project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 유저를 프로젝트에 추가
   */
  async addUserToProject({ projectId, userId, role }: AddUserToProjectData): Promise<ProjectUserDTO> {
    try {
      // 1. 이미 프로젝트에 속해있는지 확인
      const existingMember = await this.projectUserRepository.findByProjectAndUser(projectId, userId);
      if (existingMember) {
        throw new Error('User is already a member of this project');
      }

      // 2. 프로젝트가 존재하는지 확인
      const project = await this.projectRepository.findById(projectId);
      if (!project) {
        throw new Error('Project not found');
      }

      // 3. 프로젝트에 유저 추가
      const projectUser = await this.projectUserRepository.create({
        project_id: projectId,
        user_id: userId,
        role
      });

      return ProjectMapper.toProjectUserDTO(projectUser);
    } catch (error) {
      throw new Error(`Failed to add user to project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 프로젝트에서 유저 제거
   */
  async removeUserFromProject(projectId: string, userId: string, requesterId: string): Promise<void> {
    try {
      // 1. 요청자의 권한 확인 (owner 또는 admin만 가능)
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole || (requesterRole.role !== UserRole.OWNER && requesterRole.role !== UserRole.ADMIN)) {
        throw new Error('Insufficient permissions to remove users');
      }

      // 2. 제거하려는 유저가 owner인지 확인 (owner는 제거 불가)
      const targetUser = await this.projectUserRepository.findByProjectAndUser(projectId, userId);
      if (!targetUser) {
        throw new Error('User is not a member of this project');
      }
      if (targetUser.role === UserRole.OWNER) {
        throw new Error('Cannot remove the project owner');
      }

      // 3. 유저 제거
      await this.projectUserRepository.delete(targetUser.id);

      // 4. 제거된 프로젝트가 유저의 현재 프로젝트인 경우 null로 설정
      const userProfile = await this.userRepository.findById(userId);
      if (userProfile && userProfile.current_project_id === projectId) {
        await this.userRepository.updateCurrentProject(userId, null);
      }
    } catch (error) {
      throw new Error(`Failed to remove user from project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 유저의 프로젝트 권한 변경
   */
  async updateUserRole({ projectId, userId, newRole }: UpdateUserRoleData, requesterId: string): Promise<ProjectUserDTO> {
    try {
      // 1. 요청자의 권한 확인 (owner만 가능)
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole || requesterRole.role !== UserRole.OWNER) {
        throw new Error('Only project owner can change user roles');
      }

      // 2. 대상 유저 확인
      const targetUser = await this.projectUserRepository.findByProjectAndUser(projectId, userId);
      if (!targetUser) {
        throw new Error('User is not a member of this project');
      }

      // 3. owner 권한 양도 시 특별 처리
      if (newRole === UserRole.OWNER) {
        // 현재 owner를 admin으로 변경
        requesterRole.updateRole(UserRole.ADMIN);
        await this.projectUserRepository.save(requesterRole);
      }

      // 4. 권한 업데이트
      targetUser.updateRole(newRole);
      const savedUser = await this.projectUserRepository.save(targetUser);
      return ProjectMapper.toProjectUserDTO(savedUser);
    } catch (error) {
      throw new Error(`Failed to update user role: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 프로젝트의 모든 멤버 조회 (사용자 정보 포함)
   */
  async getProjectMembers(projectId: string): Promise<ProjectUserDTO[]> {
    const projectUsersWithUsers = await this.projectUserRepository.findByProjectWithUsers(projectId);
    return projectUsersWithUsers.map(pu => ProjectMapper.toProjectUserDTO(pu));
  }

  /**
   * 유저가 속한 모든 프로젝트 조회 (페이지네이션 없음)
   */
  async getUserProjects(userId: string): Promise<ProjectDTO[]> {
    const projectUsers = await this.projectUserRepository.findByUser(userId);
    const projectIds = projectUsers.map(pu => pu.project_id);

    if (projectIds.length === 0) {
      return [];
    }

    // 프로젝트 정보들을 가져옴
    const projects = await Promise.all(
      projectIds.map(id => this.projectRepository.findById(id))
    );

    return projects
      .filter(p => p !== null)
      .map(p => ProjectMapper.toDTO(p as Project));
  }

  /**
   * 유저가 속한 모든 프로젝트 조회 (페이지네이션 포함)
   */
  async getUserProjectsPaginated(userId: string, options?: PaginationOptions): Promise<PaginatedResponse<ProjectDTO>> {
    const result = await this.projectUserRepository.findByUserPaginated(userId, options);
    
    if (result.data.length === 0) {
      return {
        data: [],
        pagination: result.pagination
      };
    }

    // 프로젝트 정보들을 가져옴
    const projectIds = result.data.map(pu => pu.project_id);
    const projects = await Promise.all(
      projectIds.map(id => this.projectRepository.findById(id))
    );

    const projectDTOs = projects
      .filter(p => p !== null)
      .map(p => ProjectMapper.toDTO(p as Project));

    return {
      data: projectDTOs,
      pagination: result.pagination
    };
  }

  /**
   * 프로젝트 정보 업데이트
   */
  async updateProject(projectId: string, data: UpdateProjectDTO, requesterId: string): Promise<ProjectDTO> {
    try {
      // 권한 확인 (owner 또는 admin만 가능)
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole || (requesterRole.role !== UserRole.OWNER && requesterRole.role !== UserRole.ADMIN)) {
        throw new Error('Insufficient permissions to update project');
      }

      // 프로젝트 조회
      const project = await this.projectRepository.findById(projectId);
      if (!project) {
        throw new Error('Project not found');
      }

      // Entity 메서드를 사용하여 업데이트
      project.updateInfo(data.name || project.name, data.description !== undefined ? data.description : project.description);

      // onboarding_completed_at 업데이트 (if provided)
      if (data.onboarding_completed_at !== undefined) {
        const completedAt = data.onboarding_completed_at ? new Date(data.onboarding_completed_at) : null;
        project.updateOnboardingStatus(completedAt);
      }

      const savedProject = await this.projectRepository.save(project);
      
      return ProjectMapper.toDTO(savedProject);
    } catch (error) {
      throw new Error(`Failed to update project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 프로젝트 삭제 (owner만 가능)
   */
  async deleteProject(projectId: string, requesterId: string): Promise<void> {
    try {
      // 권한 확인 (owner만 가능)
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole || requesterRole.role !== UserRole.OWNER) {
        throw new Error('Only project owner can delete the project');
      }

      // 프로젝트 삭제 (관련된 project_users는 CASCADE로 자동 삭제됨)
      await this.projectRepository.delete(projectId);
    } catch (error) {
      throw new Error(`Failed to delete project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 초대 코드로 프로젝트 참여
   */
  async joinProjectByInviteCode(inviteCode: string, userId: string): Promise<ProjectDTO> {
    try {
      // 1. 초대 코드로 프로젝트 찾기
      const project = await this.projectRepository.findByInviteCode(inviteCode);
      if (!project) {
        throw new Error('Invalid invite code');
      }

      // 2. 이미 멤버인지 확인
      const existingMember = await this.projectUserRepository.findByProjectAndUser(project.id, userId);
      if (existingMember) {
        throw new Error('You are already a member of this project');
      }

      // 3. 프로젝트에 member로 추가
      await this.projectUserRepository.create({
        project_id: project.id,
        user_id: userId,
        role: UserRole.MEMBER // 초대 코드로 참여하는 유저는 기본적으로 member 권한
      });

      // 4. 참여한 프로젝트를 현재 프로젝트로 설정
      await this.userRepository.updateCurrentProject(userId, project.id);

      return ProjectMapper.toDTO(project);
    } catch (error) {
      throw new Error(`Failed to join project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 프로젝트 초대 코드 재생성 (owner/admin만 가능)
   */
  async regenerateInviteCode(projectId: string, requesterId: string): Promise<string> {
    try {
      // 권한 확인
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole || (requesterRole.role !== UserRole.OWNER && requesterRole.role !== UserRole.ADMIN)) {
        throw new Error('Insufficient permissions to regenerate invite code');
      }

      // 프로젝트 조회
      const project = await this.projectRepository.findById(projectId);
      if (!project) {
        throw new Error('Project not found');
      }

      // 새로운 초대 코드 생성
      const newInviteCode = await this.generateUniqueInviteCode();

      // Entity 메서드를 사용하여 초대 코드 업데이트
      project.regenerateInviteCode(newInviteCode);
      await this.projectRepository.save(project);

      return newInviteCode;
    } catch (error) {
      throw new Error(`Failed to regenerate invite code: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 현재 프로젝트 변경
   */
  async switchCurrentProject(projectId: string, userId: string): Promise<void> {
    try {
      // 1. 유저가 해당 프로젝트의 멤버인지 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(projectId, userId);
      if (!projectUser) {
        throw new Error('You are not a member of this project');
      }

      // 2. 프로젝트가 존재하는지 확인
      const project = await this.projectRepository.findById(projectId);
      if (!project) {
        throw new Error('Project not found');
      }

      // 3. 현재 프로젝트 업데이트
      await this.userRepository.updateCurrentProject(userId, projectId);
    } catch (error) {
      throw new Error(`Failed to switch project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 현재 프로젝트 해제
   */
  async clearCurrentProject(userId: string): Promise<void> {
    try {
      await this.userRepository.updateCurrentProject(userId, null);
    } catch (error) {
      throw new Error(`Failed to clear current project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 유저의 현재 프로젝트 가져오기
   */
  async getCurrentProject(userId: string): Promise<ProjectDTO | null> {
    try {
      // 1. 유저 정보 조회
      const user = await this.userRepository.findById(userId);
      if (!user || !user.current_project_id) {
        return null;
      }

      // 2. 현재 프로젝트가 설정되어 있으면 프로젝트 정보 조회
      const project = await this.projectRepository.findById(user.current_project_id);
      if (!project) {
        // 프로젝트가 삭제되었거나 없는 경우 current_project_id를 null로 설정
        await this.userRepository.updateCurrentProject(userId, null);
        return null;
      }

      // 3. 유저가 해당 프로젝트의 멤버인지 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(project.id, userId);
      if (!projectUser) {
        // 프로젝트 멤버가 아닌 경우 current_project_id를 null로 설정
        await this.userRepository.updateCurrentProject(userId, null);
        return null;
      }

      return ProjectMapper.toDTO(project);
    } catch (error) {
      throw new Error(`Failed to get current project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 이메일로 프로젝트 초대
   */
  async inviteUserByEmail(
    projectId: string,
    email: string,
    role: ProjectUserRole,
    invitedById: string,
    redirectTo: string
  ): Promise<ProjectInvitation> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required - no valid session found');
      }

      // Edge Function을 호출하여 모든 초대 로직을 처리
      const { data, error } = await this.supabase.functions.invoke('handle-project-invitation', {
        body: {
          projectId,
          email,
          role,
          invitedById,
          redirectTo
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Edge Function error:', error);
        throw new Error(error.message || 'Failed to create invitation');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (!data?.success || !data?.invitation) {
        throw new Error('Invalid response from invitation handler');
      }

      // Edge Function에서 반환된 초대 정보로 ProjectInvitation 객체 생성
      const invitation = ProjectInvitation.fromData({
        id: data.invitation.id,
        project_id: projectId,
        email: data.invitation.email,
        role: data.invitation.role,
        invited_by_id: invitedById,
        token: data.invitation.token,
        status: 'pending',
        expires_at: data.invitation.expires_at,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        accepted_at: null
      });

      console.log('Invitation created successfully via Edge Function:', data.message);
      
      return invitation;
    } catch (error) {
      throw new Error(`Failed to invite user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Edge Function을 통한 이메일 초대
   */
  private async sendSupabaseInvitation(email: string, token: string, projectName: string, redirectTo: string): Promise<void> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required - no valid session found');
      }

      const { data, error } = await this.supabase.functions.invoke('invite-user-by-email', {
        body: {
          email,
          redirectTo: `${redirectTo}/invite`,
          data: {
            project_name: projectName,
            invitation_token: token
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Edge Function error:', error);
        // 이메일 발송 실패해도 초대는 생성된 상태로 유지
      } else {
        console.log('Invitation sent successfully:', data);
      }
    } catch (error) {
      console.error('Failed to send invitation:', error);
      // 이메일 발송 실패해도 초대는 생성된 상태로 유지
    }
  }

  /**
   * 프로젝트 초대 목록 조회
   */
  async getProjectInvitations(
    projectId: string,
    requesterId: string,
    status?: InvitationStatus
  ): Promise<any[]> {
    try {
      // 권한 확인 (owner 또는 admin만 가능)
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole || (requesterRole.role !== UserRole.OWNER && requesterRole.role !== UserRole.ADMIN)) {
        throw new Error('Insufficient permissions to view invitations');
      }

      // 초대 목록 조회 (초대한 사람 정보 포함)
      return await this.projectInvitationRepository.findByProjectWithDetails(projectId, status);
    } catch (error) {
      throw new Error(`Failed to get invitations: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 초대 취소
   */
  async cancelInvitation(invitationId: string, requesterId: string): Promise<void> {
    try {
      // 초대 정보 조회
      const invitation = await this.projectInvitationRepository.findById(invitationId);
      if (!invitation) {
        throw new Error('Invitation not found');
      }

      // 권한 확인 (프로젝트 owner/admin만 가능)
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(invitation.project_id, requesterId);
      if (!requesterRole || (requesterRole.role !== UserRole.OWNER && requesterRole.role !== UserRole.ADMIN)) {
        throw new Error('Insufficient permissions to cancel invitation');
      }

      // 초대 취소
      await this.projectInvitationRepository.cancelInvitation(invitationId);
    } catch (error) {
      throw new Error(`Failed to cancel invitation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 초대 수락 (토큰 기반)
   */
  async acceptInvitationByToken(token: string, userId: string): Promise<ProjectDTO> {
    try {
      // 1. 토큰으로 초대 찾기
      const invitation = await this.projectInvitationRepository.findByToken(token);
      if (!invitation) {
        throw new Error('Invalid invitation token');
      }

      // 2. 초대 상태 확인
      if (!invitation.canBeAccepted()) {
        throw new Error('This invitation cannot be accepted');
      }

      // 3. 이메일 확인
      const user = await this.userRepository.findById(userId);
      console.log('User email:', user?.email);
      console.log('Invitation email:', invitation.email);
      
      if (!user || user.email !== invitation.email) {
        throw new Error(`This invitation is for a different email address. Invitation is for ${invitation.email} but you are logged in as ${user?.email}`);
      }

      // 4. 이미 멤버인지 확인
      const existingMember = await this.projectUserRepository.findByProjectAndUser(invitation.project_id, userId);
      if (existingMember) {
        // 이미 멤버인 경우 초대를 accepted로 변경만 하고 프로젝트 반환
        invitation.accept();
        await this.projectInvitationRepository.save(invitation);
        
        const project = await this.projectRepository.findById(invitation.project_id);
        if (!project) throw new Error('Project not found');
        return ProjectMapper.toDTO(project);
      }

      // 5. 프로젝트에 추가
      try {
        await this.projectUserRepository.create({
          project_id: invitation.project_id,
          user_id: userId,
          role: invitation.role
        });
      } catch (error: any) {
        // 409 에러는 이미 멤버인 경우이므로 무시
        if (error?.code !== '23505') { // PostgreSQL unique violation
          throw error;
        }
        console.log('User is already a member of the project');
      }

      // 6. 초대 상태 업데이트
      try {
        invitation.accept();
        console.log('Updating invitation:', {
          id: invitation.id,
          status: invitation.status,
          accepted_at: invitation.accepted_at,
          email: invitation.email
        });
        await this.projectInvitationRepository.save(invitation);
      } catch (error: any) {
        console.error('Failed to update invitation status:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        // 초대 업데이트 실패는 무시하고 계속 진행
        // (사용자는 이미 프로젝트에 추가됨)
      }

      // 7. 사용자의 current_project_id 업데이트
      try {
        await this.userRepository.updateCurrentProject(userId, invitation.project_id);
        console.log('Updated user current_project_id to:', invitation.project_id);
      } catch (error) {
        console.error('Failed to update current_project_id:', error);
        // 실패해도 계속 진행 (필수 작업은 아님)
      }

      // 8. 프로젝트 정보 반환
      const project = await this.projectRepository.findById(invitation.project_id);
      if (!project) throw new Error('Project not found');
      
      return ProjectMapper.toDTO(project);
    } catch (error) {
      throw new Error(`Failed to accept invitation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 사용자의 초대 목록 조회
   */
  async getUserInvitations(email: string, status?: InvitationStatus): Promise<ProjectInvitationWithProjectDTO[]> {
    try {
      const invitations = await this.projectInvitationRepository.findByEmailWithProject(email, status);
      return invitations.map(invitation => ProjectInvitationMapper.toProjectInvitationWithProjectDTO(invitation));
    } catch (error) {
      throw new Error(`Failed to get user invitations: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 토큰으로 초대 정보 조회
   */
  async getInvitationByToken(token: string): Promise<ProjectInvitation | null> {
    try {
      return await this.projectInvitationRepository.findByToken(token);
    } catch (error) {
      console.error('Error getting invitation by token:', error);
      return null;
    }
  }

  /**
   * 프로젝트 플랜 정보 조회
   */
  async getProjectPlanInfo(projectId: string, requesterId: string): Promise<any> {
    try {
      // 1. 권한 확인 - 프로젝트 멤버만 조회 가능
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole) {
        throw new Error('You are not a member of this project');
      }

      // 2. 직접 데이터베이스에서 플랜 정보 조회 (service_role로)
      const { data: planData, error: planError } = await this.supabase
        .from('project_plans')
        .select('plan_name, status, expires_at, max_documents, max_members, monthly_credits, is_on_demand')
        .eq('project_id', projectId)
        .single();

      if (planError) {
        throw planError;
      }

      // 3. 사용량 통계 직접 조회
      const currentMonth = new Date().toISOString().slice(0, 7) + '-01'; // YYYY-MM-01 format

      // 문서 수 조회
      const { count: documentCount, error: docError } = await this.supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId);

      if (docError) throw docError;

      // 멤버 수 조회  
      const { count: memberCount, error: memberError } = await this.supabase
        .from('project_users')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId);

      if (memberError) throw memberError;

      // 이번 달 채팅 수 직접 조회
      const startOfMonth = new Date(currentMonth);
      const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0, 23, 59, 59);

      // Count chats for this project this month
      // First get all rooms for this project
      const { data: rooms, error: roomsError } = await this.supabase
        .from('rooms')
        .select('id')
        .eq('project_id', projectId);

      if (roomsError) throw roomsError;

      const roomIds = rooms?.map(r => r.id) || [];

      let chatCount = 0;
      if (roomIds.length > 0) {
        const { count, error: chatError } = await this.supabase
          .from('chats')
          .select('*', { count: 'exact', head: true })
          .in('room_id', roomIds)
          .gte('created_at', startOfMonth.toISOString())
          .lte('created_at', endOfMonth.toISOString())
          .eq('role', 'user'); // Only count user messages, not AI responses

        if (chatError) throw chatError;
        chatCount = count || 0;
      }

      // 4. 사용률 계산
      const documentUsagePercent = planData.max_documents 
        ? Math.round((documentCount || 0) / planData.max_documents * 100)
        : 0;
      
      const memberUsagePercent = planData.max_members
        ? Math.round((memberCount || 0) / planData.max_members * 100)
        : 0;

      // 5. 결과 구성
      const result = {
        name: planData.plan_name,
        display_name: planData.plan_name === 'free' ? 'Free' : 
                     planData.plan_name === 'team' ? 'Team' : 'Enterprise',
        status: planData.status,
        expires_at: planData.expires_at,
        limits: {
          max_documents: planData.max_documents,
          max_members: planData.max_members,
          monthly_credits: planData.monthly_credits
        },
        usage: {
          document_count: documentCount || 0,
          member_count: memberCount || 0,
          document_usage_percent: documentUsagePercent,
          member_usage_percent: memberUsagePercent
        },
        is_on_demand: planData.is_on_demand
      };

      return result;
    } catch (error) {
      throw new Error(`Failed to get project plan info: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 플랜 제한 확인 (외부에서 호출용)
   */
  async checkPlanLimit(projectId: string, limitType: 'document' | 'member' | 'chat'): Promise<boolean> {
    try {
      const { data, error } = await this.supabase.rpc('check_plan_limit', {
        p_project_id: projectId,
        p_limit_type: limitType
      });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error(`Failed to check plan limit: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false; // 에러 시 기본적으로 제한으로 처리
    }
  }

  /**
   * 채팅 사용량 증가
   */
  async incrementChatUsage(projectId: string): Promise<void> {
    try {
      const { error } = await this.supabase.rpc('increment_chat_usage', {
        p_project_id: projectId
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error(`Failed to increment chat usage: ${error instanceof Error ? error.message : 'Unknown error'}`);
      // 사용량 증가 실패는 무시하고 계속 진행
    }
  }

  /**
   * 프로젝트용 추천 질문 가져오기
   */
  async getSuggestedQuestions(projectId: string, userId: string): Promise<string[]> {
    try {
      // 1. 권한 확인 - 프로젝트 멤버만 조회 가능
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, userId);
      if (!requesterRole) {
        throw new Error('You are not a member of this project');
      }

      // 2. Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Authentication required - no valid session found');
      }

      // 3. Edge Function 호출
      const { data, error } = await this.supabase.functions.invoke('get-suggested-questions', {
        body: { projectId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      if (data?.success && data?.questions) {
        return data.questions;
      }

      return [];
    } catch (error) {
      console.error(`Failed to get suggested questions: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return [];
    }
  }

  /**
   * 프로젝트 크레딧 정보 조회
   */
  async getProjectCreditsInfo(projectId: string, requesterId: string): Promise<any> {
    try {
      // 1. 권한 확인 - 프로젝트 멤버만 조회 가능
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole) {
        throw new Error('You are not a member of this project');
      }

      // 2. 크레딧 정보 조회
      const { data, error } = await this.supabase.rpc('get_project_credits_info', {
        p_project_id: projectId
      });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      throw new Error(`Failed to get project credits info: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 프로젝트 크레딧 사용 내역 조회
   */
  async getCreditTransactions(
    projectId: string, 
    requesterId: string, 
    options?: { 
      startDate?: string, 
      endDate?: string,
      transactionType?: string,
      limit?: number 
    }
  ): Promise<any[]> {
    try {
      // 1. 권한 확인 - 프로젝트 멤버만 조회 가능
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole) {
        throw new Error('You are not a member of this project');
      }

      // 2. 크레딧 트랜잭션 조회
      let query = this.supabase
        .from('credit_transactions')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (options?.startDate) {
        query = query.gte('created_at', options.startDate);
      }
      if (options?.endDate) {
        query = query.lte('created_at', options.endDate);
      }
      if (options?.transactionType) {
        query = query.eq('transaction_type', options.transactionType);
      }
      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      throw new Error(`Failed to get credit transactions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 크레딧 사용 내역 집계
   */
  async getCreditUsageBreakdown(projectId: string, requesterId: string): Promise<any> {
    try {
      // 1. 권한 확인 - 프로젝트 멤버만 조회 가능
      const requesterRole = await this.projectUserRepository.findByProjectAndUser(projectId, requesterId);
      if (!requesterRole) {
        throw new Error('You are not a member of this project');
      }

      // 2. 현재 월의 크레딧 사용 내역 가져오기
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: transactions, error } = await this.supabase
        .from('credit_transactions')
        .select('transaction_type, credits_used')
        .eq('project_id', projectId)
        .gte('created_at', startOfMonth.toISOString());

      if (error) {
        throw error;
      }

      // 3. 타입별 집계
      const breakdown: Record<string, number> = {};
      let total = 0;

      if (transactions) {
        for (const transaction of transactions) {
          const type = transaction.transaction_type;
          const credits = transaction.credits_used;
          
          breakdown[type] = (breakdown[type] || 0) + credits;
          total += credits;
        }
      }

      // 4. 포맷팅
      const formattedBreakdown = [
        { type: 'chat', label: 'Chat', credits: breakdown['chat'] || 0 },
        { type: 'web_import', label: 'Web Import', credits: breakdown['web_import'] || 0 },
        { type: 'youtube_import', label: 'YouTube Import', credits: breakdown['youtube_import'] || 0 },
        { type: 'pdf_import', label: 'PDF Import', credits: breakdown['pdf_import'] || 0 },
        { type: 'word_import', label: 'Word Import', credits: breakdown['word_import'] || 0 },
        { type: 'excel_import', label: 'Excel Import', credits: breakdown['excel_import'] || 0 },
        { type: 'ppt_import', label: 'PowerPoint Import', credits: breakdown['ppt_import'] || 0 },
        { type: 'image_import', label: 'Image Import', credits: breakdown['image_import'] || 0 }
      ];

      return {
        breakdown: formattedBreakdown,
        total,
        transactions: transactions?.length || 0
      };
    } catch (error) {
      throw new Error(`Failed to get credit usage breakdown: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

}
